// ── ROOMS FROM WALLS ─────────────────────────────────────────────────────────
// Finds the rooms of a plan as the regions ENCLOSED BY ITS WALLS.
//
// ══ WHY NOT THE DETECTOR ════════════════════════════════════════════════════
//
// Rooms used to come from Roboflow's room boxes, which has two fatal problems:
// the model frequently detects none at all (leaving an import with walls and no
// rooms), and a detected box is a loose rectangle that does not line up with
// the walls around it.
//
// The walls already say exactly where the rooms are — a room IS the space they
// enclose. Deriving rooms from them means every room is bounded by real walls,
// no room can overlap another, and the result does not depend on the detector
// noticing anything. The detector is then used only for what it is good at:
// telling us which enclosed region is the kitchen and which is the bathroom.
//
// ══ HOW ═════════════════════════════════════════════════════════════════════
//
// The walls are axis-aligned, so their coordinates induce a grid: every
// vertical wall contributes an x line, every horizontal wall a z line. Between
// consecutive lines lies a cell that no wall passes through, so a cell is
// entirely inside or entirely outside a room.
//
// Two neighbouring cells belong to the same room when no wall separates them.
// Flood-filling that relation labels connected regions; the region touching the
// outside is the exterior, and every other region is a room.

/** Sorted unique values, with near-duplicates collapsed. */
function gridLines(values, tolerance) {
  const sorted = [...values].sort((a, b) => a - b);
  const out = [];
  for (const v of sorted) {
    if (!out.length || v - out[out.length - 1] > tolerance) out.push(v);
  }
  return out;
}

/**
 * Is a wall blocking the way between two cells?
 *
 * `pos` is the line between them, and `lo`..`hi` the extent they share. A wall
 * blocks only if it lies on that line AND spans the shared extent — a wall with
 * a doorway in it still blocks, because a doorway is a hole in a wall, not the
 * absence of one. (Openings are carried separately and cut into the wall later.)
 */
function blocked(walls, axis, pos, lo, hi, tol) {
  const mid = (lo + hi) / 2;
  for (const w of walls) {
    if (w.axis !== axis) continue;
    if (Math.abs(w.at - pos) > tol) continue;
    if (w.from <= mid + tol && w.to >= mid - tol) return true;
  }
  return false;
}

/**
 * Derives rooms from wall segments.
 *
 * @param {Array} segments  world-metre walls {axis, at, from, to}
 * @param {object} [opts]
 * @param {number} [opts.tolerance]  metres of slack when matching a wall to a line
 * @param {number} [opts.minArea]    ignore regions smaller than this (m²)
 * @param {number} [opts.minSide]    ignore regions thinner than this (m)
 * @returns {Array<{x0,x1,z0,z1,area}>} room rectangles, world metres
 */
export function roomsFromWalls(segments, opts = {}) {
  const { tolerance = 0.35, minArea = 1.2, minSide = 0.8 } = opts;
  if (!segments || segments.length < 4) return [];

  const xs = gridLines(
    segments.filter(s => s.axis === 'v').map(s => s.at)
      .concat(segments.filter(s => s.axis === 'h').flatMap(s => [s.from, s.to])),
    tolerance,
  );
  const zs = gridLines(
    segments.filter(s => s.axis === 'h').map(s => s.at)
      .concat(segments.filter(s => s.axis === 'v').flatMap(s => [s.from, s.to])),
    tolerance,
  );
  if (xs.length < 2 || zs.length < 2) return [];

  const nx = xs.length - 1, nz = zs.length - 1;
  const idx = (i, j) => j * nx + i;

  // Label every cell by flood fill, treating walls as barriers. Label 0 is
  // unassigned; the region reached from outside the grid is marked exterior.
  const label = new Int32Array(nx * nz).fill(-1);
  let next = 0;
  const regions = [];

  for (let j0 = 0; j0 < nz; j0++) {
    for (let i0 = 0; i0 < nx; i0++) {
      if (label[idx(i0, j0)] >= 0) continue;
      const id = next++;
      const cells = [];
      const stack = [[i0, j0]];
      label[idx(i0, j0)] = id;
      let touchesEdge = false;

      while (stack.length) {
        const [i, j] = stack.pop();
        cells.push([i, j]);
        if (i === 0 || j === 0 || i === nx - 1 || j === nz - 1) {
          // Only an EDGE cell with no wall between it and the outside makes the
          // region exterior — otherwise the outermost room inside the shell
          // would be mistaken for the outdoors.
          if (i === 0      && !blocked(segments, 'v', xs[0],  zs[j], zs[j + 1], tolerance)) touchesEdge = true;
          if (i === nx - 1 && !blocked(segments, 'v', xs[nx], zs[j], zs[j + 1], tolerance)) touchesEdge = true;
          if (j === 0      && !blocked(segments, 'h', zs[0],  xs[i], xs[i + 1], tolerance)) touchesEdge = true;
          if (j === nz - 1 && !blocked(segments, 'h', zs[nz], xs[i], xs[i + 1], tolerance)) touchesEdge = true;
        }

        const tryMove = (di, dj) => {
          const ni = i + di, nj = j + dj;
          if (ni < 0 || nj < 0 || ni >= nx || nj >= nz) return;
          if (label[idx(ni, nj)] >= 0) return;
          const wall = di !== 0
            ? blocked(segments, 'v', xs[Math.max(i, ni)], zs[j], zs[j + 1], tolerance)
            : blocked(segments, 'h', zs[Math.max(j, nj)], xs[i], xs[i + 1], tolerance);
          if (wall) return;
          label[idx(ni, nj)] = id;
          stack.push([ni, nj]);
        };
        tryMove(1, 0); tryMove(-1, 0); tryMove(0, 1); tryMove(0, -1);
      }
      regions.push({ id, cells, exterior: touchesEdge });
    }
  }

  const rooms = [];
  for (const r of regions) {
    if (r.exterior) continue;
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, area = 0;
    const parts = [];
    for (const [i, j] of r.cells) {
      x0 = Math.min(x0, xs[i]); x1 = Math.max(x1, xs[i + 1]);
      z0 = Math.min(z0, zs[j]); z1 = Math.max(z1, zs[j + 1]);
      area += (xs[i + 1] - xs[i]) * (zs[j + 1] - zs[j]);
      parts.push({ x0: xs[i], x1: xs[i + 1], z0: zs[j], z1: zs[j + 1] });
    }
    if (area < minArea) continue;
    // A region no thicker than a wall is not a space — it is the sliver left
    // between two wall lines that did not quite coincide. Area alone misses
    // these: a long thin strip can be several square metres and still be a
    // crack rather than a room.
    if (Math.min(x1 - x0, z1 - z0) < minSide) continue;
    // `parts` are the region's actual cells. A room is often NOT a rectangle —
    // a living space commonly wraps around a hall or a cupboard — and its
    // bounding box would then swallow that neighbour whole, producing rooms
    // that overlap. Callers that need true coverage use `parts`; `x0..z1` is
    // only the extent.
    rooms.push({ x0, x1, z0, z1, area, parts });
  }
  // Largest first, so the label matcher assigns the big obvious rooms before
  // the leftovers.
  return rooms.sort((a, b) => b.area - a.area);
}

/**
 * Names each derived room from whichever detected room box covers it most.
 *
 * The geometry is ours; the detector only supplies the word. A room it did not
 * see keeps a generic type rather than being dropped.
 *
 * @param rooms     from roomsFromWalls
 * @param detected  world-metre boxes {cls, x0, x1, z0, z1}
 */
export function labelRooms(rooms, detected) {
  const used = new Set();
  return rooms.map(r => {
    let best = null, bestOverlap = 0;
    for (const d of detected || []) {
      if (used.has(d)) continue;
      const ix = Math.max(0, Math.min(r.x1, Math.max(d.x0, d.x1)) - Math.max(r.x0, Math.min(d.x0, d.x1)));
      const iz = Math.max(0, Math.min(r.z1, Math.max(d.z0, d.z1)) - Math.max(r.z0, Math.min(d.z0, d.z1)));
      const overlap = ix * iz;
      if (overlap > bestOverlap) { bestOverlap = overlap; best = d; }
    }
    // At least a third of the room must be covered before its name is trusted.
    if (best && bestOverlap > r.area * 0.33) {
      used.add(best);
      return { ...r, cls: best.cls };
    }
    return { ...r, cls: null };
  });
}
